import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import { supabase } from './supabase';
import type { VersionPolicy } from '../utils/versionGate';

/**
 * Lecture de la version installée et de la politique de versions publiée (`app_versions`).
 *
 * LA TABLE EST LISIBLE SANS SESSION (policy `to anon`, voir `0042_app_versions.sql`) : une version
 * obsolète l'est aussi avant connexion, et le verdict doit pouvoir tomber sur l'écran
 * d'authentification.
 */

const CLE_CACHE = 'app_version_policy';
const CLE_BANNIERE_IGNOREE = 'app_version_banner_dismissed';

/**
 * Version du BINAIRE, pas celle du dépôt. `app.json` dit ce qu'on croit livrer ; le
 * `CFBundleShortVersionString` dit ce qui tourne. Le jour où les deux divergent est précisément
 * celui où le verdict doit être juste.
 */
export function versionInstallee(): string | null {
  return Application.nativeApplicationVersion;
}

/**
 * Politique publiée pour la plateforme courante. `null` = aucune ligne (plateforme non semée, web) :
 * une absence, pas une erreur. Les erreurs, elles, remontent — c'est au hook d'appliquer le
 * fail-open, et les étouffer ici rendrait un refus de lecture indiscernable d'une table vide.
 */
export async function fetchVersionPolicy(): Promise<VersionPolicy | null> {
  const { data, error } = await supabase
    .from('app_versions')
    .select('latest_version, minimum_version, store_url')
    .eq('platform', Platform.OS)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const ligne = data as { latest_version: string; minimum_version: string; store_url: string };
  return {
    latestVersion: ligne.latest_version,
    minimumVersion: ligne.minimum_version,
    storeUrl: ligne.store_url,
  };
}

/**
 * Dernière politique connue. C'est elle qui rend le blocage effectif sur un appareil sans réseau au
 * lancement — sans cache, il suffirait de couper le wifi pour désarmer le coupe-circuit.
 */
export async function lirePolitiqueEnCache(): Promise<VersionPolicy | null> {
  try {
    const brut = await AsyncStorage.getItem(CLE_CACHE);
    return brut ? (JSON.parse(brut) as VersionPolicy) : null;
  } catch {
    // Écriture interrompue, format changé entre deux versions : on repart de rien plutôt que de
    // faire échouer le lancement.
    return null;
  }
}

export async function ecrirePolitiqueEnCache(politique: VersionPolicy): Promise<void> {
  try {
    await AsyncStorage.setItem(CLE_CACHE, JSON.stringify(politique));
  } catch {
    // Le cache est un confort, pas une condition : son échec ne doit rien interrompre.
  }
}

/** Version dont la bannière a été rejetée. Mémorisée PAR VERSION, jamais « une fois pour toutes ». */
export async function lireBanniereIgnoree(): Promise<string | null> {
  try {
    return await AsyncStorage.getItem(CLE_BANNIERE_IGNOREE);
  } catch {
    return null;
  }
}

export async function ecrireBanniereIgnoree(version: string): Promise<void> {
  try {
    await AsyncStorage.setItem(CLE_BANNIERE_IGNOREE, version);
  } catch {
    // Sans persistance, la bannière réapparaîtra au prochain lancement — dégradation acceptable.
  }
}
